<template>
  <div>
    <CartPaymentProcessingOverlay :show="cartStore.isProcessing" />
    <div v-if="cartStore.cartData.cartConfig.paymentScreen === 'elements'">
      <div id="stripeElements" v-show="!setupError">
        <div id="express-checkout-element" class="mb-4"></div>
        <div
          id="express-error-message"
          class="alert alert-soft-danger mb-4"
          v-if="expressErrorMessage"
        >
          {{ expressErrorMessage }}
        </div>
        <span class="divider-center mb-4">OR</span>
        <div id="link-authentication-element"></div>
        <form @submit.prevent="handleSubmit">
          <div id="payment-element" class="mb-3"></div>
          <div v-if="errorMessage" class="alert alert-soft-danger mb-4">
            {{ errorMessage }}
          </div>
          <div class="d-grid mb-2">
            <button
              type="submit"
              class="btn btn-primary btn-lg"
              :disabled="cartStore.isProcessing || !isSetupComplete"
            >
              Pay Now
            </button>
          </div>
        </form>
      </div>
      <div v-if="setupError" class="alert alert-soft-danger">
        {{ setupError }}
      </div>
    </div>
    <div v-else id="stripeCheckout"></div>
  </div>
</template>

<script setup>
import { defineProps, onMounted, onBeforeUnmount, inject, ref } from 'vue'
import { Payments } from '../helpers/payments'
import { useCartStore } from '../stores/cartStore'
import CartPaymentProcessingOverlay from './CartPaymentProcessingOverlay.vue'
import { captureError } from '../services/sentry'

const props = defineProps({
  clientSecret: {
    type: String,
    required: true
  },
  defaultValues: {
    type: Object,
    default: () => ({})
  }
})

// State and store initialization
const cartStore = useCartStore()
const formValues = inject('formValues', null)
const isSetupComplete = ref(false)
const errorMessage = ref('')
const expressErrorMessage = ref('')
const setupError = ref('')
let elements = null
let stripe = null

// Default billing details based on form values
const stripeDefaultValues = {
  billingDetails: {
    name: formValues
      ? [formValues.first_name, formValues.middle_name, formValues.last_name]
          .filter(Boolean)
          .join(' ')
      : '',
    email: formValues?.email_address || ''
  }
}

// Handle form submission
async function handleSubmit() {
  if (!stripe || !elements || !props.clientSecret) {
    const error = new Error(
      'Payment system not initialized. Please refresh and try again.'
    )
    errorMessage.value = error.message
    captureError(error, {
      type: 'stripe_initialization_error',
      hasStripe: !!stripe,
      hasElements: !!elements,
      hasClientSecret: !!props.clientSecret
    })
    return
  }

  cartStore.setIsProcessing(true)
  errorMessage.value = ''

  try {
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: Payments.RETURN_URL
      }
    })

    if (error) {
      errorMessage.value = error.message
      captureError(error, {
        type: 'stripe_payment_error',
        stripeErrorType: error.type,
        cartConfig: cartStore.cartData?.cartConfig
      })
    } else {
      cartStore.setStripeClientSecret(null)
    }
  } catch (e) {
    console.error('Error processing payment:', e)
    errorMessage.value = 'An unexpected error occurred. Please try again.'
    captureError(e, {
      type: 'stripe_unexpected_error',
      cartConfig: cartStore.cartData?.cartConfig
    })
  } finally {
    cartStore.setIsProcessing(false)
  }
}

// Handle express checkout errors
function handleExpressError(error) {
  expressErrorMessage.value = error.message
  captureError(error, {
    type: 'stripe_express_checkout_error',
    cartConfig: cartStore.cartData?.cartConfig
  })
}

// Lifecycle hooks for Stripe setup
onMounted(async () => {
  if (!props.clientSecret) {
    setupError.value = 'Payment system configuration is missing'
    return
  }

  try {
    await handleStripeSetup()
    isSetupComplete.value = true
  } catch (error) {
    console.error('Error during Stripe setup:', error)
    setupError.value =
      'Unable to initialize payment system. Please try refreshing the page.'
  }
})

onBeforeUnmount(() => {
  try {
    Payments.unmountStripeComponents()
    if (cartStore.stripeCheckoutInstance) {
      cartStore.stripeCheckoutInstance.unmount()
      cartStore.setStripeCheckoutInstance(null)
    }
  } catch (error) {
    console.error('Error during cleanup:', error)
  }
})

// Function to mount Stripe checkout
async function mountStripeCheckout() {
  if (!cartStore.stripeCheckoutInstance) {
    const checkout = await Payments.setupStripeCheckout(
      props.clientSecret,
      'stripeCheckout'
    )
    cartStore.setStripeCheckoutInstance(checkout)
  } else {
    await cartStore.stripeCheckoutInstance.mount('#stripeCheckout')
  }
}

// Function to handle express checkout confirmation
async function handleExpressConfirm(event) {
  try {
    const { error: submitError } = await elements.submit()

    if (submitError) {
      handleExpressError(submitError)
      return
    }

    const { error } = await stripe.confirmPayment({
      elements,
      clientSecret: props.clientSecret,
      confirmParams: {
        return_url: Payments.RETURN_URL
      }
    })

    if (error) {
      handleExpressError(error)
    } else {
      cartStore.setStripeClientSecret(null)
    }
  } catch (error) {
    console.error('Error in express checkout confirmation:', error)
    handleExpressError(error)
  }
}

// Function to handle Stripe setup based on cart data
async function handleStripeSetup() {
  if (cartStore.cartData.cartConfig.paymentScreen === 'elements') {
    console.log('Setting up Stripe elements with config:', {
      paymentScreen: cartStore.cartData.cartConfig.paymentScreen,
      hasStripe: !!stripe,
      hasClientSecret: !!props.clientSecret
    })

    stripe = await Payments.getStripe()
    if (!stripe) {
      const error = new Error('Failed to initialize Stripe')
      console.error('Stripe initialization failed:', error)
      captureError(error, {
        type: 'stripe_setup_error',
        cartConfig: cartStore.cartData?.cartConfig
      })
      throw error
    }

    const appearance = Payments.getAppearance()
    const options = {
      loader: 'auto',
      appearance,
      clientSecret: props.clientSecret
    }

    console.log('Creating Stripe elements with options:', {
      ...options,
      clientSecret: '[REDACTED]'
    })

    elements = stripe.elements(options)

    // Setup express checkout with enhanced logging
    const expressCheckoutElement = elements.create('expressCheckout', {
      buttonType: {
        applePay: 'plain',
        googlePay: 'plain',
        paypal: 'paypal'
      },
      buttonHeight: 45,
      buttonTheme: {
        applePay: 'black',
        googlePay: 'black',
        paypal: 'black'
      }
    })

    console.log('Mounting express checkout element')
    await expressCheckoutElement.mount('#express-checkout-element')

    expressCheckoutElement.on('click', (event) => {
      console.log('Express checkout clicked:', event)
      event.resolve({ emailRequired: true })
    })

    expressCheckoutElement.on('confirm', (event) => {
      console.log('Express checkout confirm event:', event)
      handleExpressConfirm(event)
    })

    // Setup payment element with enhanced logging
    console.log('Creating payment element')
    const paymentElement = elements.create('payment', {
      layout: {
        type: 'accordion',
        defaultCollapsed: false,
        radios: true,
        spacedAccordionItems: false
      },
      defaultValues: stripeDefaultValues
    })

    console.log('Mounting payment element')
    await paymentElement.mount('#payment-element')
  } else {
    await mountStripeCheckout()
  }
}
</script>
